import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router"

type QueryValue = string | string[]

export function useTableParams<T extends Record<string, QueryValue>>(
	defaultValues: T,
) {
	type ExtraKey = keyof T & string
	type QueryKey = "page" | "sort" | "query" | ExtraKey

	const defaultInit = useMemo(
		() => ({
			page: "1",
			query: "",
			sort: [],
			...defaultValues,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(defaultValues)],
	)
	const [searchParams, setSearchParams] = useSearchParams(defaultInit)

	const page = searchParams.get("page") ?? "1"
	const query = searchParams.get("query") ?? ""
	const sort = searchParams.getAll("sort").filter(Boolean)

	const extraValues = Object.fromEntries(
		Object.entries(defaultValues).map(([key, value]) => [
			key,
			Array.isArray(value)
				? searchParams.getAll(key)
				: String(searchParams.get(key) ?? ""),
		]),
	) as { [K in ExtraKey]: T[K] }

	const set = useCallback(
		(...pairs: Array<[QueryKey, QueryValue]>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev)
				let resetSortAndPage = false
				for (const [key, value] of pairs) {
					resetSortAndPage = resetSortAndPage || !["sort", "page"].includes(key)
					if (Array.isArray(value)) {
						next.delete(key)
						value.forEach((v) => next.append(key, v))
					} else {
						next.set(key, value)
					}
				}

				if (resetSortAndPage) {
					next.delete("sort")
					if (defaultValues.sort !== undefined) {
						const sortValue = defaultValues.sort
						if (Array.isArray(sortValue)) {
							sortValue.forEach((value) => next.append("sort", value))
						} else {
							next.set("sort", sortValue as string)
						}
					}
					next.set("page", "1")
				}

				return next
			})
		},
		[defaultValues.sort, setSearchParams],
	)

	return { set, page, sort, query, ...extraValues }
}
